import React, { useEffect, useState } from "react";
import { addDestination } from "../../apis/firebase";
import {
  DestinationType,
  FACEBOOK_LOGIN_PERMISSIONS,
  INSTAGRAM_LOGIN_PERMISSIONS,
} from "../../constants";
import { addUserDestination } from "../../state/User";
import { isDefined, verifyRealUser } from "../helpers";

export function addFacebookDestination(
  user: User,
  onError: Function,
  onSuccess: Function,
  instagram: boolean
) {
  async function handleTokens(shortLiveAccessToken: string) {
    try {
      const res = await fetch(`/api/f?t=${shortLiveAccessToken}`);
      const data = await res.json();
      if (res.status === 200) {
        if (data.pages.length === 0) {
          onError(
            <>
              Linking to Facebook/Instagram here at Contempas means using
              Facebook pages. Your account doesn't manage any or you didn't
              authorize access. If you keep seeing this error you probably have
              to go to{" "}
              <i>Settings &amp; privacy → Settings → Business integrations</i>{" "}
              and remove the Contempas app. Then try again.
            </>
          );
        } else {
          if (instagram) {
            let foundInstagramAccount = false;
            await Promise.all(
              data.pages.map(async (fbPage) => {
                if (fbPage.connected_instagram_account) {
                  foundInstagramAccount = true;
                  const newDestination = await addDestination(
                    user,
                    DestinationType.INSTAGRAM,
                    fbPage,
                    {
                      name:
                        fbPage.connected_instagram_account.name ||
                        "Instagram account connected to " + fbPage.name,
                    }
                  );
                  addUserDestination(newDestination);
                }
              })
            );
            if (!foundInstagramAccount) {
              onError(
                <>
                  You didn't authorize access to an Instagram account. If you
                  keep seeing this error you probably have to go to{" "}
                  <i>
                    Settings &amp; privacy → Settings → Business integrations
                  </i>{" "}
                  and remove the Contempas app. Then try again.
                </>
              );
            }
          } else {
            await Promise.all(
              data.pages.map(async (fbPage) => {
                const newDestination = await addDestination(
                  user,
                  DestinationType.FACEBOOK,
                  fbPage,
                  {
                    name: fbPage.name,
                  }
                );
                addUserDestination(newDestination);
              })
            );
          }
          onSuccess();
        }
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error(err);
      onError("There is a problem linking your Facebook account.");
    }
  }
  FB.getLoginStatus(async function (response) {
    function ok(response) {
      if (response.authResponse && response.authResponse.accessToken) {
        handleTokens(response.authResponse.accessToken);
      } else {
        onError("Not able to login or use that Facebook app.");
      }
    }
    if (response.status !== "connected") {
      FB.login(ok, {
        scope: instagram
          ? INSTAGRAM_LOGIN_PERMISSIONS
          : FACEBOOK_LOGIN_PERMISSIONS,
        auth_type: "rerequest",
      });
    } else {
      ok(response);
    }
  }, true);
}

export function AddFacebookDestination({
  onError,
  user,
  instagram = false,
}: {
  onError: Function;
  user: User;
  instagram?: boolean;
}) {
  const isFBAPIAvailable = isDefined(FB);
  const [inProgress, setProgressFlag] = useState(false);

  return isFBAPIAvailable ? (
    <button
      className="secondary fz08"
      onClick={() => {
        verifyRealUser(user, () => {
          setProgressFlag(true);
          addFacebookDestination(
            user,
            (err) => {
              setProgressFlag(false);
              onError([err, instagram ? "/help/instagram" : "/help/facebook"]);
            },
            () => {
              setProgressFlag(false);
            },
            instagram
          );
        });
      }}
      disabled={inProgress}
    >
      {inProgress ? "⏳ " : null}
      <span className="vam">
        {instagram ? "Link an Instagram account" : "Link a Facebook account"}
      </span>
    </button>
  ) : null;
}
